/* eslint-disable complexity */
/* eslint-disable react/display-name */
import {
  CustomColumnInputType,
  CustomFormulaColumnInputType,
  IMandatoryColumn,
} from '@scale/shared/types/corp/speedAuditColumnConfig';
import TextWrapEditComponent from './tbsaTextEdit';
import {
  TextWrapComponent,
  MarkdownWrapComponent,
  AttempterWrapComponent,
  IsEditedChip,
  NumberSliderComponent,
  BooleanCheckboxComponent,
  SelectComponent,
  RatingComponent,
  RatingEditComponent,
  FixedColumnWrapComponent,
  TextDiffComponent,
} from './tbsaCellComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GridAlignment,
  GridCellParams,
  GridColumns,
  GridRenderCellParams,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-pro';
import { GetDefaultColumnsInput, ResponseField } from './tbsaTypes';
import _ from 'lodash';
import { AttemptCustomColumn } from '@scale/shared/types/corp/attemptAudit';
import { AuditResult } from '@scale/shared/defaults';
import { TextCollectionFieldType } from '@scale/shared/types/params/textcollection';

import { Tooltip } from 'react-scale/components/design-system/product';
import { InteractionStepType } from '@scale/shared/types/chat/params';
import { ThreadedChatResponse } from '@scale/shared/types/chat/response';

const AUDIT_RATINGS = [
  AuditResult.Terrible,
  AuditResult.Bad,
  AuditResult.Ok,
  AuditResult.Good,
  AuditResult.Perfect,
];

const ALLOWED_INTERACTION_STEP_TYPES = [
  InteractionStepType.PromptInput,
  InteractionStepType.ModelResponseEditor,
  InteractionStepType.MultiTurnContinue,
];

export const LockedColumnHeader = (header: string) => (
  <span style={{ display: 'flex', alignItems: 'center' }}>
    {header}
    {
      <FontAwesomeIcon
        icon="lock"
        style={{ marginLeft: 8, height: 10 }}
        color="rgba(0, 0, 0, 0.4)"
      />
    }
  </span>
);

const MandatoryColumnHeader = (
  header: string,
  field: string,
  mandatoryColumns: IMandatoryColumn,
) => {
  const mandatoryColumn = _.get(mandatoryColumns, field);
  return (
    <Tooltip
      title={
        _.get(mandatoryColumn, 'isMandatory')
          ? `${header} is mandatory ${
              mandatoryColumn.isConditional
                ? `if the column ${_.startCase(
                    (mandatoryColumn?.conditionalColumn ?? '').replace('_editable', ''),
                  )} ${mandatoryColumn.condition} ${mandatoryColumn.conditionValue}`
                : ''
            }`
          : ''
      }
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {header}
        {_.get(mandatoryColumn, 'isMandatory') && (
          <FontAwesomeIcon
            icon="asterisk"
            style={{ marginLeft: 8, height: 10 }}
            color="rgba(0, 0, 0, 0.4)"
          />
        )}

        {_.get(mandatoryColumn, 'isMandatory') && _.get(mandatoryColumn, 'isConditional') && (
          <FontAwesomeIcon
            icon="asterisk"
            style={{ marginLeft: 8, height: 10 }}
            color="rgba(0, 0, 0, 0.4)"
          />
        )}
      </span>
    </Tooltip>
  );
};

export const getDefaultColumns = ({
  canEditResponses,
  isComplete,
  editableColumns,
  fixedColumns,
  numTextAttachments,
  responseFields,
  hasFairPrompt,
  setRatingSelected,
  styles,
  updatePastActions,
  possibleErrorCategories,
  mandatoryColumns,
  customFormulaColumns,
}: GetDefaultColumnsInput): GridColumns => {
  const columns: GridColumns = [
    {
      field: 'attempted_at',
      headerName: `Last Attempted At`,
      renderHeader: () => LockedColumnHeader('Last Attempted At'),
      width: 200,
    },
    {
      field: 'attempted_by',
      headerName: `Last Attempted By`,
      renderHeader: () => LockedColumnHeader('Last Attempted By'),
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <AttempterWrapComponent params={params} styles={styles} />
      ),
    },
  ];

  for (let i = 0; i < numTextAttachments; i++) {
    columns.push({
      field: `text_attachment_${i}`,
      headerName: `Attachments (${i + 1})`,
      renderHeader: () => LockedColumnHeader(`Attachments (${i + 1})`),
      width: 400,
      renderCell: p => <MarkdownWrapComponent params={p} styles={styles} />,
    });
  }
  for (const responseField of _.uniq(responseFields)) {
    columns.push({
      field: responseField,
      headerName: responseField,
      type: 'text',
      renderHeader: () =>
        canEditResponses && !isComplete ? (
          <div>{responseField}</div>
        ) : (
          LockedColumnHeader(responseField)
        ),
      editable: canEditResponses && !isComplete,
      width: 250,
      renderCell: p => <TextWrapComponent params={p} styles={styles} />,
      renderEditCell: (params: GridRenderEditCellParams<string>) => (
        <TextWrapEditComponent
          params={params}
          updatePastActions={updatePastActions}
          styles={styles}
          taskResponseCell={true}
        />
      ),
    });
    columns.push({
      field: `${responseField}_diff`,
      headerName: `${responseField} Diff`,
      width: 250,
      renderHeader: () => LockedColumnHeader(`${responseField} Diff`),
      renderCell: p => <TextDiffComponent params={p} styles={styles} />,
    });
  }
  if (hasFairPrompt) {
    columns.push({
      field: 'fairPrompt',
      headerName: 'prompt',
      type: 'text',
      renderHeader: () => LockedColumnHeader('prompt'),
      width: 300,
      renderCell: p => <TextWrapComponent params={p} styles={styles} />,
    });
  }
  columns.push({
    field: 'rating',
    headerName: 'Rating',
    type: 'singleSelect',
    renderHeader: () =>
      !isComplete
        ? MandatoryColumnHeader('Rating', 'rating', mandatoryColumns)
        : LockedColumnHeader('Rating'),
    valueOptions: AUDIT_RATINGS,
    renderCell: (params: GridRenderCellParams) => (
      <RatingComponent params={params} styles={styles} setRatingSelected={setRatingSelected} />
    ),
    renderEditCell: (params: GridRenderCellParams) => (
      <RatingEditComponent params={params} styles={styles} updatePastActions={updatePastActions} />
    ),
    editable: !isComplete,
    width: 350,
  });
  columns.push({
    field: 'errorCategories',
    headerName: 'Error Categories',
    renderHeader: () =>
      !isComplete
        ? MandatoryColumnHeader('Error Categories', 'errorCategories', mandatoryColumns)
        : LockedColumnHeader('Error Categories'),
    renderCell: (params: GridRenderCellParams) => (
      <SelectComponent
        params={params}
        editable={!isComplete}
        updatePastActions={updatePastActions}
        styles={styles}
        selectOptions={possibleErrorCategories}
        multipleSelect={true}
        label="Error Categories"
        placeholder="Error"
      />
    ),
    width: 250,
  });
  columns.push({
    field: 'notes',
    headerName: 'Notes',
    type: 'text',
    renderHeader: () =>
      !isComplete
        ? MandatoryColumnHeader('Notes', 'notes', mandatoryColumns)
        : LockedColumnHeader('Notes'),
    editable: !isComplete,
    width: 200,
    renderCell: p => <TextWrapComponent params={p} styles={styles} />,
    renderEditCell: (params: GridRenderEditCellParams<string>) => (
      <TextWrapEditComponent
        params={params}
        updatePastActions={updatePastActions}
        styles={styles}
        taskResponseCell={false}
      />
    ),
  });
  columns.push({
    field: 'taskerFeedback',
    headerName: 'Tasker Feedback',
    type: 'text',
    renderHeader: () =>
      !isComplete
        ? MandatoryColumnHeader('Tasker Feedback', 'taskerFeedback', mandatoryColumns)
        : LockedColumnHeader('Tasker Feedback'),
    editable: !isComplete,
    width: 200,
    renderCell: p => <TextWrapComponent params={p} styles={styles} />,
    renderEditCell: (params: GridRenderEditCellParams<string>) => (
      <TextWrapEditComponent
        params={params}
        updatePastActions={updatePastActions}
        styles={styles}
        taskResponseCell={false}
      />
    ),
  });
  if (!_.isEmpty(editableColumns)) {
    const editableColDef = editableColumns.map(col => {
      const columnConfigs: Record<string, any> = {};
      switch (col.inputType as CustomColumnInputType | CustomFormulaColumnInputType) {
        case CustomColumnInputType.Rating:
          columnConfigs.type = 'number';
          columnConfigs.editable = false;
          columnConfigs.renderCell = (params: GridRenderCellParams) => (
            <NumberSliderComponent
              params={params}
              maxRating={col.maxRating ?? 10}
              editable={!isComplete}
              updatePastActions={updatePastActions}
              styles={styles}
            />
          );
          break;
        case CustomColumnInputType.Boolean:
          columnConfigs.type = 'boolean';
          columnConfigs.editable = false;
          columnConfigs.renderCell = (params: GridRenderCellParams) => (
            <BooleanCheckboxComponent
              params={params}
              editable={!isComplete}
              updatePastActions={updatePastActions}
              styles={styles}
            />
          );
          break;
        case CustomColumnInputType.Select:
          columnConfigs.type = 'select';
          columnConfigs.editable = false;
          columnConfigs.renderCell = (params: GridRenderCellParams) => (
            <SelectComponent
              params={params}
              selectOptions={(col.selectOptions ?? '')
                .trim()
                .split(/,\s*/g)
                .map((el: $FIXME) => el.trim())
                .filter((el: $FIXME) => el !== '')}
              editable={!isComplete}
              updatePastActions={updatePastActions}
              styles={styles}
            />
          );
          break;
        case CustomColumnInputType.MultiSelect:
          columnConfigs.type = 'select';
          columnConfigs.editable = false;
          columnConfigs.renderCell = (params: GridRenderCellParams) => (
            <SelectComponent
              params={params}
              selectOptions={(col.selectOptions ?? '')
                .trim()
                .split(/,\s*/g)
                .map((el: $FIXME) => el.trim())
                .filter((el: $FIXME) => el !== '')}
              editable={!isComplete}
              updatePastActions={updatePastActions}
              styles={styles}
              multipleSelect={true}
            />
          );
          break;
        case CustomFormulaColumnInputType.Average:
          columnConfigs.type = 'average';
          columnConfigs.editable = false;
          columnConfigs.renderHeader = () => LockedColumnHeader(col.name);
          columnConfigs.valueGetter = (params: GridRenderCellParams) => {
            const averageColumnsConfig = customFormulaColumns.find(
              column => column.name === col.name,
            );
            const customValues = _.get(averageColumnsConfig, 'selectColumns', []).map(
              columnName => {
                return _.get(params.row, columnName, 0);
              },
            );
            return _.mean(customValues).toString();
          };
          columnConfigs.renderCell = (params: GridRenderCellParams) => (
            <TextWrapComponent params={params} styles={styles} />
          );
          break;
        case CustomFormulaColumnInputType.Difference:
          columnConfigs.type = 'diff';
          columnConfigs.editable = false;
          columnConfigs.renderHeader = () => LockedColumnHeader(col.name);
          columnConfigs.valueGetter = (params: GridRenderCellParams) => {
            const diffColumnsConfig = customFormulaColumns.find(column => column.name === col.name);
            const diffColumn = {
              originalText: '',
              newText: '',
            };
            _.get(diffColumnsConfig, 'selectColumns', []).forEach(columnName => {
              _.set(
                diffColumn,
                !diffColumn.originalText ? 'originalText' : 'newText',
                _.get(params.row, columnName, ''),
              );
            });
            return diffColumn;
          };
          columnConfigs.renderCell = (params: GridRenderCellParams) => {
            return <TextDiffComponent params={params} styles={styles} />;
          };
          break;
        default:
          columnConfigs.editable = !isComplete;
          columnConfigs.type = 'text';
          columnConfigs.renderCell = (p: GridRenderEditCellParams) => (
            <TextWrapComponent params={p} styles={styles} />
          );
          columnConfigs.renderEditCell = (params: GridRenderEditCellParams<string>) => (
            <TextWrapEditComponent
              params={params}
              updatePastActions={updatePastActions}
              styles={styles}
              taskResponseCell={false}
            />
          );
      }

      return {
        field: `${col.name}_editable`,
        headerName: col.name,
        renderHeader: () =>
          !isComplete
            ? MandatoryColumnHeader(col.name, `${col.name}_editable`, mandatoryColumns)
            : LockedColumnHeader(col.name),
        align: 'left' as GridAlignment,
        headerAlign: 'left' as GridAlignment,
        width: 250,
        type: 'string',
        ...columnConfigs,
      };
    });
    columns.push(...editableColDef);
  }
  if (!_.isEmpty(fixedColumns)) {
    const fixedColDef = fixedColumns.map(name => ({
      field: `${name}_fixed`,
      headerName: name,
      renderHeader: () => LockedColumnHeader(name),
      editable: false,
      width: 250,
      renderCell: (p: GridRenderEditCellParams) => (
        <FixedColumnWrapComponent params={p} styles={styles} />
      ),
    }));
    columns.push(...fixedColDef);
  }
  columns.push({
    field: 'isEdited',
    headerName: 'Edits Made?',
    width: 200,
    renderCell: p => <IsEditedChip params={p} styles={styles} />,
  });
  return columns;
};

export const getCellClassName = (params: GridCellParams, apiRef: any): string => {
  const { value, field } = params;
  const filters = apiRef.current.state.filter.filterModel.items;
  const quickFilters = apiRef.current.state.filter.filterModel.quickFilterValues;

  if (
    !_.isNil(value) &&
    value.toString() &&
    _.some(quickFilters, filter => _.lowerCase(value.toString()).includes(_.lowerCase(filter)))
  ) {
    return 'highlightedCell';
  }
  if (
    !_.isNil(value) &&
    value.toString() &&
    _.some(
      filters,
      filter =>
        filter.columnField === field &&
        _.lowerCase(value.toString()).includes(_.lowerCase(filter.value)) &&
        filter.value,
    )
  ) {
    return 'highlightedCell';
  }
  return 'standardCell';
};

export const levenshtein = (a: string, b: string): number => {
  const matrix = Array.from({ length: a.length }).map(() =>
    Array.from({ length: b.length }).map(() => 0),
  );

  for (let i = 0; i < a.length; i++) matrix[i][0] = i + 1;

  for (let i = 0; i < b.length; i++) matrix[0][i] = i + 1;

  for (let j = 0; j < b.length; j++)
    for (let i = 0; i < a.length; i++)
      matrix[i][j] = Math.min(
        (i === 0 ? 0 : matrix[i - 1][j]) + 1,
        (j === 0 ? 0 : matrix[i][j - 1]) + 1,
        (i === 0 || j === 0 ? 0 : matrix[i - 1][j - 1]) + (a[i] === b[j] ? 0 : 1),
      );

  // adding the diff between lenghts gives a fundamentally better result
  return matrix[a.length - 1][b.length - 1] + Math.abs(a.length - b.length);
};

/*
  Takes in a response annotation object and returns a list of ResponseField objects for all of the field's responses and its nested responses.
    * field: the response annotation object that we want to flatten
    * fieldNamePrefix: an accumulator for the name of the column holding this response value in the data table
    * fieldPathPrefix: an accumulator for the path to the response value
*/
export const getNestedFields = (
  field: Record<string, any>,
  fieldPathPrefix: string,
  fieldNamePrefix: string,
): ResponseField[] => {
  if (field.type === TextCollectionFieldType.FieldSet) {
    const fieldId = field.field_id;
    const fieldObjs = [];
    for (const i in field.response) {
      const response = field.response[i];
      for (const subfield of Object.values(response)) {
        fieldObjs.push(
          getNestedFields(
            subfield as Record<string, any>,
            `${fieldPathPrefix}['${fieldId}'].response[${i}]`,
            `${fieldNamePrefix}${fieldId} (${i}) > `,
          ),
        );
      }
    }
    return _.flatten(fieldObjs);
  } else {
    return field.response.map((response: any, index: number) => {
      return {
        path: `${fieldPathPrefix}['${field.field_id}'].response[${index}]`,
        colName: `${fieldNamePrefix}${field.field_id} (${index})`,
        fieldName: field.field_id,
        value: response,
        type: field.type as TextCollectionFieldType,
      };
    });
  }
};

const getChatResponses = (response: ThreadedChatResponse) => {
  const responses = response.responses;
  const responseArray = _.compact(
    responses.map(step => {
      if (!ALLOWED_INTERACTION_STEP_TYPES.includes(step.type)) return null;
      return {
        path: `responses[${step.index}].output`,
        colName: `${step.type} (${step.index})`,
        fieldName: `${step.type} (${step.index})`,
        value: step.output,
        type: step.type as InteractionStepType,
      };
    }),
  );
  return responseArray;
};

// function to populate initial row values
export const getRowFromAttemptAudit = (attemptAudit: any) => {
  const newResponse = attemptAudit.newResponse;
  const oldResponse = attemptAudit.oldResponse;
  const newResponseFields =
    attemptAudit.taskProps.type === 'chat'
      ? getChatResponses(newResponse)
      : _.flatten(
          Object.values(newResponse.annotations).map((field: any) =>
            getNestedFields(field, 'annotations', ''),
          ),
        );
  const newResponseFieldsObj = Object.fromEntries(
    newResponseFields.map(field => [field.colName, field.value]),
  );
  const oldResponseFields =
    attemptAudit.taskProps.type === 'chat'
      ? getChatResponses(oldResponse)
      : _.flatten(
          Object.values(oldResponse.annotations).map((field: any) =>
            getNestedFields(field, 'annotations', ''),
          ),
        );
  const oldResponseFieldsObj = Object.fromEntries(
    oldResponseFields.map(field => [field.colName, field.value]),
  );

  const visibleFields = newResponseFields.filter(
    (field: any) =>
      field.type === TextCollectionFieldType.Text ||
      ALLOWED_INTERACTION_STEP_TYPES.includes(field.type),
  );
  const responseDiffs = Object.fromEntries(
    Object.keys(newResponseFieldsObj).map(field => {
      const diffObj = {
        newText: newResponseFieldsObj[field],
        originalText: oldResponseFieldsObj[field],
      };
      return [`${field}_diff`, diffObj];
    }),
  );

  const rawAttachments =
    attemptAudit.subtaskAttachments ?? attemptAudit.taskProps.params?.attachments ?? [];
  const textAttachments = Object.fromEntries(
    rawAttachments.map((attachment: any, index: number) => [
      `text_attachment_${index}`,
      attachment.content,
    ]),
  );
  const numTextAttachments = rawAttachments.length;

  const fairPrompt = _.get(
    attemptAudit,
    'newResponse.annotations.rlhf_history.response[0].interactions[0].prompt',
    undefined,
  );

  const customColumnValues = Object.fromEntries(
    attemptAudit.customColumns.map((col: AttemptCustomColumn) => [
      `${col.name}_editable`,
      col.value,
    ]),
  );
  const fixedColumnValues = _.mapKeys(attemptAudit.fixedColumns, (_, key) => `${key}_fixed`);
  const taskId = attemptAudit.taskId;
  const subtaskId = attemptAudit.subtaskId;
  const attemptId = attemptAudit.taskAttempt?._id ?? attemptAudit.trainingAttempt?._id;

  const result = {
    attempt_audit_id: attemptAudit._id,
    task_id: taskId,
    subtask_id: subtaskId,
    attempt_id: attemptId,
    task_links: `${taskId}, ${subtaskId}, ${attemptId}`,
    attempted_at: new Date(attemptAudit.attemptedAt).toLocaleString(),
    attempted_by: attemptAudit.taskAttempt?.attemptedBy ?? attemptAudit.trainingAttempt?.worker,
    ...textAttachments,
    ...newResponseFieldsObj,
    ...responseDiffs,
    oldResponseFieldsObj,
    rating: attemptAudit.result ?? attemptAudit.resultV2?.[0]?.result,
    errorCategories: _.last(attemptAudit.resultV2 as any[])?.errorCategories ?? [],
    notes: attemptAudit.comments,
    taskerFeedback: attemptAudit.taskerFeedback,
    isEdited: !_.isEqual(newResponse, oldResponse),
    lastSavedResponse: _.cloneDeep(newResponse),
    ...customColumnValues,
    ...fixedColumnValues,
    attemptAuditType: attemptAudit.auditLookup.type,
  };

  let hasFairPrompt = false;
  if (fairPrompt !== undefined) {
    hasFairPrompt = true;
    _.set(result, 'fairPrompt', fairPrompt);
  }

  return {
    result,
    visibleFields,
    numTextAttachments,
    hasFairPrompt,
    isChatTask: attemptAudit.taskProps.type === 'chat',
  };
};
